// What's in season in Colorado, month by month. Compiled from Colorado
// growers' harvest calendars (Front Range, Western Slope, San Luis Valley
// and the Arkansas Valley). Months are 1–12; `stored` marks months when the
// crop comes from Colorado storage or greenhouses rather than the field.

export interface SeasonalFood {
  name: string;
  emoji: string;
  months: number[];
  stored?: number[];
  /** A Colorado claim to fame, shown when the food is at its peak. */
  note?: string;
}

const range = (a: number, b: number) => Array.from({ length: b - a + 1 }, (_, i) => a + i);

export const COLORADO_PRODUCE: SeasonalFood[] = [
  { name: "Asparagus", emoji: "🌱", months: [5, 6] },
  { name: "Rhubarb", emoji: "🌿", months: [5, 6] },
  { name: "Spinach", emoji: "🥬", months: [5, 6, 9, 10], stored: [11, 12, 1, 2, 3, 4] },
  { name: "Lettuce & greens", emoji: "🥗", months: [5, 6, 7, 9, 10], stored: [11, 12, 1, 2, 3, 4] },
  { name: "Radishes", emoji: "🔴", months: [5, 6, 9] },
  { name: "Peas", emoji: "🫛", months: [6, 7] },
  { name: "Strawberries", emoji: "🍓", months: [6, 7] },
  { name: "Cherries", emoji: "🍒", months: [6, 7], note: "From the Western Slope orchards" },
  { name: "Apricots", emoji: "🍑", months: [7] },
  { name: "Garlic", emoji: "🧄", months: [7, 8], stored: [9, 10, 11, 12] },
  { name: "Broccoli", emoji: "🥦", months: [6, 7, 9, 10] },
  { name: "Green beans", emoji: "🫘", months: range(7, 9) },
  { name: "Cucumbers", emoji: "🥒", months: range(7, 9) },
  { name: "Zucchini & summer squash", emoji: "🥒", months: range(7, 9) },
  { name: "Sweet corn", emoji: "🌽", months: range(7, 9), note: "Olathe sweet corn is famous for a reason" },
  { name: "Peaches", emoji: "🍑", months: range(7, 9), note: "Palisade peaches: the state’s summer treasure" },
  { name: "Beets", emoji: "🟣", months: range(7, 10), stored: [11, 12, 1, 2] },
  { name: "Carrots", emoji: "🥕", months: range(7, 10), stored: [11, 12, 1, 2, 3] },
  { name: "Cabbage", emoji: "🥬", months: range(7, 10), stored: [11, 12, 1] },
  { name: "Plums", emoji: "🟣", months: [8, 9] },
  { name: "Tomatoes", emoji: "🍅", months: range(8, 10), stored: [11, 12, 1, 2, 3, 4, 5, 6, 7] },
  { name: "Eggplant", emoji: "🍆", months: [8, 9] },
  { name: "Cantaloupe & melons", emoji: "🍈", months: [8, 9], note: "Rocky Ford cantaloupe from the Arkansas Valley" },
  { name: "Watermelon", emoji: "🍉", months: [8, 9] },
  { name: "Green chile & peppers", emoji: "🌶️", months: range(8, 10), note: "Pueblo chile roasting season" },
  { name: "Onions", emoji: "🧅", months: range(8, 10), stored: [11, 12, 1, 2, 3] },
  { name: "Potatoes", emoji: "🥔", months: range(8, 10), stored: [11, 12, 1, 2, 3, 4], note: "San Luis Valley potatoes" },
  { name: "Cauliflower", emoji: "🥦", months: range(8, 10) },
  { name: "Apples", emoji: "🍎", months: range(8, 10), stored: [11, 12, 1, 2] },
  { name: "Pears", emoji: "🍐", months: range(8, 10), stored: [11] },
  { name: "Grapes", emoji: "🍇", months: [8, 9, 10] },
  { name: "Winter squash", emoji: "🎃", months: [9, 10], stored: [11, 12, 1, 2] },
  { name: "Pumpkins", emoji: "🎃", months: [9, 10] },
  { name: "Honey", emoji: "🍯", months: range(7, 9), stored: [10, 11, 12, 1, 2, 3, 4, 5, 6] },
  { name: "Mushrooms", emoji: "🍄", months: range(1, 12) },
  { name: "Microgreens", emoji: "🌱", months: range(1, 12) },
];

export interface SeasonView {
  /** In the field this month. */
  fresh: (SeasonalFood & { lastCall: boolean; justIn: boolean })[];
  /** From Colorado storage or greenhouses. */
  stored: SeasonalFood[];
  /** Starting next month. */
  comingSoon: SeasonalFood[];
}

/** Sort fresh foods so the headline crops (with a note) and first arrivals lead. */
export function seasonFor(month: number): SeasonView {
  const next = (month % 12) + 1;
  const prev = ((month + 10) % 12) + 1;
  const yearRound = (f: SeasonalFood) => f.months.length === 12;
  const fresh = COLORADO_PRODUCE.filter((f) => f.months.includes(month))
    // "Last call" only when it won't be around from storage either.
    .map((f) => ({ ...f, lastCall: !yearRound(f) && !f.months.includes(next) && !f.stored?.includes(next), justIn: !yearRound(f) && !f.months.includes(prev) }))
    .sort((a, b) => Number(yearRound(a)) - Number(yearRound(b)) || Number(Boolean(b.note)) - Number(Boolean(a.note)) || Number(b.justIn) - Number(a.justIn));
  const stored = COLORADO_PRODUCE.filter((f) => !f.months.includes(month) && f.stored?.includes(month));
  const comingSoon = COLORADO_PRODUCE.filter((f) => !f.months.includes(month) && f.months.includes(next));
  return { fresh, stored, comingSoon };
}
